import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Divider
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

/**
 * Welcome card shown before the interview starts.
 * Displays survey info and a button to begin.
 */
const WelcomeCard = ({ surveyInfo, onStart }) => {
  return (
    <Card
      sx={{
        maxWidth: 600,
        mx: 'auto',
        mt: 4,
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          textAlign: 'center'
        }}
      >
        <QuestionAnswerIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          {surveyInfo?.title || 'Research Interview'}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {surveyInfo?.description || 'We\'d love to hear about your experiences'}
        </Typography>
      </Box>

      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
          <Chip
            icon={<AccessTimeIcon />}
            label={`~${surveyInfo?.estimated_duration || 15} min`}
            variant="outlined"
            color="primary"
          />
          <Chip
            label={`${surveyInfo?.phases_count || 4} topics`}
            variant="outlined"
            color="secondary"
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          {surveyInfo?.welcome_message ||
            'Thank you for taking the time to share your insights with us. Your feedback will help shape better solutions.'}
        </Typography>

        {surveyInfo?.phase_names && surveyInfo.phase_names.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
              We'll cover:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
              {surveyInfo.phase_names.map((name, idx) => (
                <Chip
                  key={idx}
                  label={name}
                  size="small"
                  sx={{ bgcolor: 'grey.100' }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={onStart}
          startIcon={<PlayArrowIcon />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
            }
          }}
        >
          Begin Interview
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          Your responses are confidential and will be used to improve our services.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default WelcomeCard;
